import { useEffect, useState } from 'react'
import { botApi } from '../api'
import { parseProgressTimestamp } from '../utils/timeParsing'

const POLL_MS = 3000
const STATUS_PENDING = 0
const STATUS_RUNNING = 1

/** Percent of a running backtest done, or null while unknown. */
export default function useBacktestRunProgress(
  btid: string | undefined,
  startDate: string | undefined,
  endDate: string | undefined,
  running: boolean,
): number | null {
  const [state, setState] = useState<{ btid: string; pct: number | null } | null>(null)

  useEffect(() => {
    if (!running || !btid || !startDate || !endDate) return undefined
    // Same range as the Backtest tab.
    const start = Date.parse(`${startDate}T00:00:00Z`) / 1000
    const end = Date.parse(`${endDate}T23:59:59Z`) / 1000
    if (!Number.isFinite(start) || !Number.isFinite(end) || end <= start) return undefined

    let cancelled = false
    let timer: ReturnType<typeof setTimeout> | undefined
    const poll = async () => {
      try {
        const p = await botApi.getBacktestProgress(btid)
        if (cancelled) return
        if (p.status === STATUS_PENDING) {
          setState({ btid, pct: null })
        } else if (p.status === STATUS_RUNNING) {
          const ts = parseProgressTimestamp(p.progressBacktest)
          const pct = ts === null ? null : Math.max(0, Math.min(100, ((ts - start) / (end - start)) * 100))
          setState({ btid, pct })
        } else {
          return
        }
      } catch {
        // Keep the last value; try again.
      }
      if (!cancelled) timer = setTimeout(poll, POLL_MS)
    }
    void poll()
    return () => {
      cancelled = true
      if (timer) clearTimeout(timer)
    }
  }, [btid, startDate, endDate, running])

  return running && btid && state?.btid === btid ? state.pct : null
}
